"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseScientificInput } from "@/lib/tool-kit/earth-science/format";
import { toCelsius } from "@/lib/tool-kit/earth-science/temperature";
import {
  type FrostbiteBand,
  frostbiteBand,
  type WindChillResult,
  type WindUnit,
  windChill,
  windFromMph,
} from "@/lib/tool-kit/earth-science/wind-chill";

type TempUnit = "F" | "C";

/** English strings. A translated content file overrides these through `ui`. */
export const WIND_CHILL_CALCULATOR_STRINGS = {
  temperatureLabel: "Air temperature",
  temperatureUnitLabel: "Temperature unit",
  unitF: "Fahrenheit (°F)",
  unitC: "Celsius (°C)",
  windLabel: "Wind speed",
  windHint: "As measured at the standard 10 m (33 ft) anemometer height.",
  windUnitLabel: "Wind speed unit",
  unitMph: "miles per hour (mph)",
  unitKmh: "kilometres per hour (km/h)",
  unitMs: "metres per second (m/s)",
  unitKn: "knots",
  results: "Results",
  windChill: "Wind chill (feels like)",
  inOther: "Same value in {unit}",
  drop: "Feels colder than the air by",
  risk: "Frostbite risk level",
  frostbite: "Time for exposed skin to freeze",
  bandNone: "Below the risk levels",
  bandLow: "Low",
  bandModerate: "Moderate",
  bandHigh: "High",
  bandVeryHigh: "Very high",
  bandSevere: "Severe",
  bandExtreme: "Extreme",
  timeNone: "Not an issue at this wind chill",
  timeLow: "Slight increase in discomfort",
  timeModerate:
    "Risk of frostbite and hypothermia after long exposure without adequate protection",
  timeHigh: "10 to 30 minutes",
  timeVeryHigh: "5 to 10 minutes",
  timeSevere: "2 to 5 minutes",
  timeExtreme: "Less than 2 minutes",
  bandNote:
    "Risk levels and frostbite times are those Environment and Climate Change Canada publishes. In sustained winds over 50 km/h (31 mph) frostbite can happen faster than shown, and the index assumes no sun on a clear night.",
  calm: "At or below {limit} wind, the air is treated as calm and the wind chill equals the air temperature.",
  tooWarm:
    "The wind chill index is defined only at air temperatures of 50 °F (10 °C) or below. Above that, use the heat index or the air temperature.",
  workingHeading: "The working",
  stepConvert:
    "Converted: T = {tf} °F ({tc} °C) and V = {mph} mph ({kmh} km/h).",
  stepFormula:
    "WCT = 35.74 + 0.6215 T − 35.75 V^0.16 + 0.4275 T V^0.16 = 35.74 + 0.6215 × {tf} − 35.75 × {v} + 0.4275 × {tf} × {v} = {wc} °F",
  stepMetric:
    "In metric: WCT = 13.12 + 0.6215 T − 11.37 V^0.16 + 0.3965 T V^0.16 gives {wcc} °C, the same value.",
  stepV: "where V^0.16 = {mph}^0.16 = {v}.",
  tableHeading: "Wind chill chart",
  tableCorner: "Air temperature",
  chartNote:
    "Each cell is the wind chill for that air temperature and wind speed, rounded to whole degrees.",
  copyResults: "Copy results",
  copied: "Copied!",
  copyLine:
    "Wind chill {wc} at {temperature} with a {wind} wind ({risk} frostbite risk)",
  enterValues:
    "Enter an air temperature and a wind speed to see the wind chill.",
  windInvalid: "Wind speed cannot be negative.",
  belowZero:
    "That temperature is below absolute zero in the chosen unit. Check the number and the unit.",
} as const;

type Strings = typeof WIND_CHILL_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const WHOLE = { maximumFractionDigits: 0 };
const ONE = { maximumFractionDigits: 1 };
const TWO = { maximumFractionDigits: 2 };
const FOUR = { maximumFractionDigits: 4 };

const CHART_TEMPERATURES: Record<TempUnit, number[]> = {
  F: [40, 30, 20, 10, 0, -10, -20, -30, -40],
  C: [5, 0, -5, -10, -15, -20, -25, -30, -35, -40],
};
const CHART_WINDS: Record<WindUnit, number[]> = {
  mph: [5, 10, 15, 20, 25, 30, 40, 50],
  kmh: [10, 20, 30, 40, 50, 60, 70, 80],
  ms: [2, 4, 6, 8, 10, 12, 15, 20],
  kn: [5, 10, 15, 20, 25, 30, 35, 40],
};
const WIND_KEYS = {
  mph: "unitMph",
  kmh: "unitKmh",
  ms: "unitMs",
  kn: "unitKn",
} as const;
const WIND_SYMBOLS: Record<WindUnit, string> = {
  mph: "mph",
  kmh: "km/h",
  ms: "m/s",
  kn: "kn",
};
const BAND_KEYS = {
  none: ["bandNone", "timeNone"],
  low: ["bandLow", "timeLow"],
  moderate: ["bandModerate", "timeModerate"],
  high: ["bandHigh", "timeHigh"],
  veryHigh: ["bandVeryHigh", "timeVeryHigh"],
  severe: ["bandSevere", "timeSevere"],
  extreme: ["bandExtreme", "timeExtreme"],
} as const satisfies Record<FrostbiteBand, readonly (keyof Strings)[]>;

function symbolOf(unit: TempUnit): string {
  return unit === "F" ? "°F" : "°C";
}

function workingSteps(result: WindChillResult, strings: Strings, fmt: Fmt) {
  const v = result.windMph ** 0.16;
  return [
    t(strings.stepConvert, {
      tf: fmt(result.airFahrenheit, TWO),
      tc: fmt(toCelsius(result.airFahrenheit, "F"), TWO),
      mph: fmt(result.windMph, TWO),
      kmh: fmt(result.windMph * 1.609_344, TWO),
    }),
    t(strings.stepV, { mph: fmt(result.windMph, TWO), v: fmt(v, FOUR) }),
    t(strings.stepFormula, {
      tf: fmt(result.airFahrenheit, TWO),
      v: fmt(v, FOUR),
      wc: fmt(result.fahrenheit, TWO),
    }),
    t(strings.stepMetric, { wcc: fmt(result.celsius, TWO) }),
  ];
}

function chart(
  tempUnit: TempUnit,
  windUnit: WindUnit,
  fmt: Fmt
): { head: string[]; rows: string[][] } {
  const winds = CHART_WINDS[windUnit];
  return {
    head: winds.map((wind) => `${fmt(wind)} ${WIND_SYMBOLS[windUnit]}`),
    rows: CHART_TEMPERATURES[tempUnit].map((temperature) => [
      `${fmt(temperature)} ${symbolOf(tempUnit)}`,
      ...winds.map((wind) => {
        const result = windChill(temperature, tempUnit, wind, windUnit);
        return fmt(
          tempUnit === "F" ? result.fahrenheit : result.celsius,
          WHOLE
        );
      }),
    ]),
  };
}

function Results({
  temperature,
  tempUnit,
  wind,
  windUnit,
  strings,
  fmt,
}: {
  temperature: number;
  tempUnit: TempUnit;
  wind: number;
  windUnit: WindUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const result = windChill(temperature, tempUnit, wind, windUnit);
  if (result.status === "tooWarm") {
    return <Notice>{strings.tooWarm}</Notice>;
  }
  const shown = tempUnit === "F" ? result.fahrenheit : result.celsius;
  const other = tempUnit === "F" ? result.celsius : result.fahrenheit;
  const drop = tempUnit === "F" ? result.drop : (result.drop * 5) / 9;
  const [bandKey, timeKey] = BAND_KEYS[frostbiteBand(result.celsius)];
  const table = chart(tempUnit, windUnit, fmt);
  const windText = `${fmt(wind, ONE)} ${WIND_SYMBOLS[windUnit]}`;
  return (
    <>
      {result.status === "calm" ? (
        <Notice>
          {t(strings.calm, {
            limit: `${fmt(windFromMph(3, windUnit), ONE)} ${WIND_SYMBOLS[windUnit]}`,
          })}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.windChill}
          value={`${fmt(shown, ONE)} ${symbolOf(tempUnit)}`}
        />
        <StatCard
          label={t(strings.inOther, { unit: tempUnit === "F" ? "°C" : "°F" })}
          value={fmt(other, ONE)}
        />
        <StatCard
          label={strings.drop}
          value={`${fmt(drop, ONE)} ${symbolOf(tempUnit)}`}
        />
        <StatCard label={strings.risk} value={strings[bandKey]} />
      </StatGrid>
      <p className="text-foreground text-sm">
        <strong>{strings.frostbite}:</strong> {strings[timeKey]}
      </p>
      <p className={HINT}>{strings.bandNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyLine, {
          wc: `${fmt(shown, ONE)} ${symbolOf(tempUnit)}`,
          temperature: `${fmt(temperature, ONE)} ${symbolOf(tempUnit)}`,
          wind: windText,
          risk: strings[bandKey],
        })}
      />
      {result.status === "ok" ? (
        <Panel title={strings.workingHeading}>
          <ol className="list-decimal space-y-1 pl-5 text-foreground text-sm">
            {workingSteps(result, strings, fmt).map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </Panel>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.tableCorner, ...table.head]}
        rows={table.rows}
      />
      <p className={HINT}>{strings.chartNote}</p>
    </>
  );
}

function Outcome({
  temperature,
  wind,
  tempUnit,
  windUnit,
  strings,
  fmt,
}: {
  temperature: string;
  wind: string;
  tempUnit: TempUnit;
  windUnit: WindUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const typedT = parseScientificInput(temperature);
  const typedV = parseScientificInput(wind);
  if (typedT === null || typedV === null) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  if (toCelsius(typedT, tempUnit) < -273.15) {
    return <ErrorNotice>{strings.belowZero}</ErrorNotice>;
  }
  if (typedV < 0) {
    return <ErrorNotice>{strings.windInvalid}</ErrorNotice>;
  }
  return (
    <Results
      fmt={fmt}
      strings={strings}
      temperature={typedT}
      tempUnit={tempUnit}
      wind={typedV}
      windUnit={windUnit}
    />
  );
}

export default function WindChillCalculatorWidget() {
  const strings = useToolUi(WIND_CHILL_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [temperature, setTemperature] = useState("20");
  const [tempUnit, setTempUnit] = useState<TempUnit>("F");
  const [wind, setWind] = useState("15");
  const [windUnit, setWindUnit] = useState<WindUnit>("mph");

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="wcc-temperature"
          label={strings.temperatureLabel}
          numeric
          onChange={setTemperature}
          value={temperature}
        />
        <SelectField
          id="wcc-temperature-unit"
          label={strings.temperatureUnitLabel}
          onChange={setTempUnit}
          options={[
            { value: "F", label: strings.unitF },
            { value: "C", label: strings.unitC },
          ]}
          value={tempUnit}
        />
        <TextField
          hint={strings.windHint}
          id="wcc-wind"
          label={strings.windLabel}
          numeric
          onChange={setWind}
          value={wind}
        />
        <SelectField
          id="wcc-wind-unit"
          label={strings.windUnitLabel}
          onChange={setWindUnit}
          options={(Object.keys(WIND_KEYS) as WindUnit[]).map((unit) => ({
            value: unit,
            label: strings[WIND_KEYS[unit]],
          }))}
          value={windUnit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          fmt={fmt}
          strings={strings}
          temperature={temperature}
          tempUnit={tempUnit}
          wind={wind}
          windUnit={windUnit}
        />
      </section>
    </div>
  );
}
